import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { generateReplenishmentTasks } from "@/lib/admin.functions";
import {
  listReplenishmentRules, listSkuOptions, listLocationOptions,
  createReplenishmentRule, toggleReplenishmentRule, deleteReplenishmentRule,
  bulkUpsertReplenishmentRules,
} from "@/lib/replenishment.functions";
import {
  listReplenishmentTasks,
  runReplenishmentSweep,
} from "@/lib/replenishment-auto.functions";
import {
  startReplenishmentTask,
  shortCompleteReplenishmentTask,
  failReplenishmentTask,
  resourceReplenishmentTask,
} from "@/lib/replenishment-lifecycle.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Play } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ReplenishmentTaskPanel } from "@/components/replenishment/replenishment-task-panel";

export const Route = createFileRoute("/_authenticated/app/admin/replenishment")({
  component: ReplenishmentAdmin,
});

function ReplenishmentAdmin() {
  const qc = useQueryClient();
  const { tenantId, facilityId } = useScope();
  const listRulesFn = useServerFn(listReplenishmentRules);
  const listSkusFn = useServerFn(listSkuOptions);
  const listLocsFn = useServerFn(listLocationOptions);
  const createRuleFn = useServerFn(createReplenishmentRule);
  const toggleRuleFn = useServerFn(toggleReplenishmentRule);
  const deleteRuleFn = useServerFn(deleteReplenishmentRule);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCsv, setBulkCsv] = useState("sku_code,location_code,min,max\n");
  const bulkFn = useServerFn(bulkUpsertReplenishmentRules);
  const [skuId, setSkuId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [minQty, setMinQty] = useState("10");
  const [maxQty, setMaxQty] = useState("50");

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ["replen-rules", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => listRulesFn({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
  });

  const { data: skus = [] } = useQuery({
    queryKey: ["rp-skus", tenantId],
    enabled: !!tenantId,
    queryFn: () => listSkusFn({ data: { tenantId: tenantId! } }),
  });
  const { data: locations = [] } = useQuery({
    queryKey: ["rp-locations", facilityId],
    enabled: !!facilityId,
    queryFn: () => listLocsFn({ data: { facilityId: facilityId! } }),
  });

  const skuLabel = (id: string) => {
    const s = skus.find((x) => x.id === id);
    return s ? `${s.sku_code} · ${s.name}` : "—";
  };
  const locCode = (id: string) => locations.find((l) => l.id === id)?.code ?? "—";

  const reset = () => { setSkuId(""); setLocationId(""); setMinQty("10"); setMaxQty("50"); };

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId || !facilityId) return;
    if (!skuId || !locationId) return toast.error("SKU and forward location required");
    try {
      await createRuleFn({ data: {
        tenantId, facilityId, skuId, forwardLocationId: locationId,
        minQty: Number(minQty) || 0, maxQty: Number(maxQty) || 0,
      } });
      toast.success("Rule added");
      setOpen(false); reset();
      qc.invalidateQueries({ queryKey: ["replen-rules", tenantId, facilityId] });
    } catch (err) { toast.error((err as Error).message); }
  };

  const toggle = async (id: string, active: boolean) => {
    try {
      await toggleRuleFn({ data: { id, active: !active } });
      qc.invalidateQueries({ queryKey: ["replen-rules", tenantId, facilityId] });
    } catch (err) { toast.error((err as Error).message); }
  };
  const remove = async (id: string) => {
    if (!confirm("Delete rule?")) return;
    try {
      await deleteRuleFn({ data: { id } });
      qc.invalidateQueries({ queryKey: ["replen-rules", tenantId, facilityId] });
    } catch (err) { toast.error((err as Error).message); }
  };

  const generateReplenFn = useServerFn(generateReplenishmentTasks);
  const sweepFn = useServerFn(runReplenishmentSweep);
  const listTasksFn = useServerFn(listReplenishmentTasks);
  const startFn = useServerFn(startReplenishmentTask);
  const shortFn = useServerFn(shortCompleteReplenishmentTask);
  const failFn = useServerFn(failReplenishmentTask);
  const resourceFn = useServerFn(resourceReplenishmentTask);

  const { data: tasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: ["replen-tasks", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => listTasksFn({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
    refetchInterval: 15_000,
  });

  const generateTasks = async () => {
    if (!facilityId) return;
    try {
      const res = await runServerFn(
        () => generateReplenFn({ data: { facilityId } }),
        "Generate replenishment",
      );
      toast.success(`Created ${res.created} replenishment tasks`);
      qc.invalidateQueries({ queryKey: ["replen-tasks", tenantId, facilityId] });
    } catch {
      // toast handled
    }
  };

  const runSweep = async () => {
    if (!tenantId) return;
    try {
      const res = await runServerFn(
        () => sweepFn({ data: { tenantId } }),
        "Running replenishment sweep",
      );
      toast.success(`Scanned ${res.rulesScanned} rules, created ${res.tasksCreated} tasks`);
      qc.invalidateQueries({ queryKey: ["replen-tasks", tenantId, facilityId] });
    } catch { /* toast handled */ }
  };

  const runBulk = async () => {
    if (!tenantId || !facilityId) return;
    const lines = bulkCsv.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length < 2) return toast.error("Add at least one data row");
    const rows: { skuCode: string; locationCode: string; minQty: number; maxQty: number }[] = [];
    for (let i = 1; i < lines.length; i++) {
      const parts = lines[i].split(",").map((p) => p.trim());
      if (parts.length < 4) return toast.error(`Row ${i}: need 4 columns`);
      const [sku, loc, min, max] = parts;
      const mn = Number(min), mx = Number(max);
      if (!Number.isFinite(mn) || !Number.isFinite(mx)) return toast.error(`Row ${i}: min/max must be numbers`);
      rows.push({ skuCode: sku, locationCode: loc, minQty: mn, maxQty: mx });
    }
    try {
      const res = await bulkFn({ data: { tenantId, facilityId, rows } });
      if (res.errors.length) {
        toast.error(`${res.errors.length} error(s); first: ${res.errors[0].error}`);
        return;
      }
      toast.success(`Upserted ${res.upserted} rules`);
      setBulkOpen(false);
      qc.invalidateQueries({ queryKey: ["replen-rules", tenantId, facilityId] });
    } catch (e: any) { toast.error(e?.message ?? "Bulk failed"); }
  };

  const openCount = tasks.filter((t: any) => t.status === "open").length;
  const inProgressCount = tasks.filter((t: any) => t.status === "in_progress").length;
  const missingSourceCount = tasks.filter((t: any) => !t.source_location_id).length;

  const refreshTasks = () => qc.invalidateQueries({ queryKey: ["replen-tasks", tenantId, facilityId] });

  const startTask = async (id: string) => {
    try { await startFn({ data: { taskId: id } }); toast.success("Task started"); refreshTasks(); }
    catch (err) { toast.error((err as Error).message); }
  };
  const shortTask = async (id: string, requested: number) => {
    const raw = prompt(`Short complete — qty completed (0..${requested - 1}):`, "0");
    if (raw == null) return;
    const qty = Number(raw);
    const reason = prompt("Reason (required):", "Source short");
    if (!reason) return;
    try { await shortFn({ data: { taskId: id, qtyCompleted: qty, reason } }); toast.success("Marked short"); refreshTasks(); }
    catch (err) { toast.error((err as Error).message); }
  };
  const failTask = async (id: string) => {
    const code = prompt("Failure code:", "blocked");
    if (!code) return;
    const note = prompt("Note (optional):", "") ?? "";
    try { await failFn({ data: { taskId: id, reasonCode: code, reasonNote: note || undefined } }); toast.success("Task failed"); refreshTasks(); }
    catch (err) { toast.error((err as Error).message); }
  };
  const resourceTask = async (id: string) => {
    try {
      const res = await resourceFn({ data: { taskId: id } });
      toast.success(res.newSourceId ? "New source assigned" : "No eligible source found");
      refreshTasks();
    } catch (err) { toast.error((err as Error).message); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Replenishment Rules</h2>
          <p className="text-sm text-muted-foreground">
            Min/max thresholds per forward-pick location. Run generator to create move tasks for any shortfalls.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runSweep} disabled={!tenantId}>
            <Play className="h-4 w-4 mr-2" /> Run sweep
          </Button>
          <Button variant="outline" onClick={generateTasks} disabled={!facilityId}>
            <Play className="h-4 w-4 mr-2" /> Generate tasks
          </Button>
          <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" disabled={!tenantId || !facilityId}>Bulk import</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Bulk upsert rules (CSV)</DialogTitle></DialogHeader>
              <p className="text-xs text-muted-foreground">
                Columns: <code>sku_code,location_code,min,max</code>. Existing rules at the same forward-pick location are updated.
              </p>
              <Textarea rows={10} className="font-mono text-xs"
                value={bulkCsv} onChange={(e) => setBulkCsv(e.target.value)} />
              <DialogFooter><Button onClick={runBulk}>Apply</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
            <DialogTrigger asChild>
              <Button disabled={!tenantId || !facilityId}><Plus className="h-4 w-4 mr-2" />New rule</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add replenishment rule</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div className="space-y-2">
                  <Label>SKU</Label>
                  <Select value={skuId} onValueChange={setSkuId}>
                    <SelectTrigger><SelectValue placeholder="Choose SKU" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {skus.map((s) => <SelectItem key={s.id} value={s.id}>{s.sku_code} · {s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Forward-pick location</Label>
                  <Select value={locationId} onValueChange={setLocationId}>
                    <SelectTrigger><SelectValue placeholder="Choose location" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {locations.filter((l) => l.type === "storage" || l.type === "staging").map((l) =>
                        <SelectItem key={l.id} value={l.id}>{l.code} · {l.type}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Min qty (trigger)</Label>
                    <Input type="number" value={minQty} onChange={(e) => setMinQty(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Max qty (refill to)</Label>
                    <Input type="number" value={maxQty} onChange={(e) => setMaxQty(e.target.value)} />
                  </div>
                </div>
                <DialogFooter><Button type="submit">Add rule</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="border rounded-lg bg-card p-3">
          <div className="text-xs text-muted-foreground">Open tasks</div>
          <div className="text-2xl font-semibold tabular-nums">{openCount}</div>
        </div>
        <div className="border rounded-lg bg-card p-3">
          <div className="text-xs text-muted-foreground">In-flight</div>
          <div className="text-2xl font-semibold tabular-nums">{inProgressCount}</div>
        </div>
        <div className="border rounded-lg bg-card p-3">
          <div className="text-xs text-muted-foreground">No source suggested</div>
          <div className="text-2xl font-semibold tabular-nums">{missingSourceCount}</div>
        </div>
      </div>

      <div className="border rounded-lg bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>SKU</TableHead>
            <TableHead>Forward location</TableHead>
            <TableHead className="text-right">Min</TableHead>
            <TableHead className="text-right">Max</TableHead>
            <TableHead>Status</TableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : rules.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No rules yet.</TableCell></TableRow>
            ) : rules.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="text-sm">{skuLabel(r.sku_id)}</TableCell>
                <TableCell className="text-sm font-mono">{locCode(r.forward_location_id)}</TableCell>
                <TableCell className="text-right tabular-nums">{r.min_qty}</TableCell>
                <TableCell className="text-right tabular-nums">{r.max_qty}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.active ? "active" : "off"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => toggle(r.id, r.active)}>
                    {r.active ? "Disable" : "Enable"}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(r.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Open & In-Flight Tasks</h3>
        <div className="border rounded-lg bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>SKU</TableHead>
              <TableHead>From</TableHead>
              <TableHead>To</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {tasksLoading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : tasks.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">No open replenishment tasks.</TableCell></TableRow>
              ) : tasks.map((t: any) => (
                <TableRow key={t.id}>
                  <TableCell className="text-sm">{skuLabel(t.sku_id)}</TableCell>
                  <TableCell className="text-sm font-mono">{t.source_location_id ? locCode(t.source_location_id) : <span className="text-muted-foreground italic">unassigned</span>}</TableCell>
                  <TableCell className="text-sm font-mono">{locCode(t.forward_location_id)}</TableCell>
                  <TableCell className="text-right tabular-nums">{t.qty_completed}/{t.qty_requested}</TableCell>
                  <TableCell className="text-xs">{t.status}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString()}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {t.status === "open" && (
                      <Button variant="ghost" size="sm" onClick={() => startTask(t.id)}>Start</Button>
                    )}
                    {!t.source_location_id && (
                      <Button variant="ghost" size="sm" onClick={() => resourceTask(t.id)}>Re-source</Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => shortTask(t.id, t.qty_requested)}>Short</Button>
                    <Button variant="ghost" size="sm" onClick={() => failTask(t.id)}>Fail</Button>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="ghost" size="sm">Why</Button>
                      </PopoverTrigger>
                      <PopoverContent align="end" className="w-[420px]">
                        <ReplenishmentTaskPanel taskId={t.id} />
                      </PopoverContent>
                    </Popover>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
